import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const CONSENT_STORAGE_KEY = "pv_google_consent_v2";

type ConsentPreferences = {
  analytics: boolean;
  ads: boolean;
  updated_at: string;
};

const readPreferences = (): ConsentPreferences | null => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<ConsentPreferences>;
    if (typeof parsed.analytics !== "boolean" || typeof parsed.ads !== "boolean") return null;

    return {
      analytics: parsed.analytics,
      ads: parsed.ads,
      updated_at: typeof parsed.updated_at === "string" ? parsed.updated_at : "",
    };
  } catch {
    return null;
  }
};

const applyGoogleConsent = (preferences: Pick<ConsentPreferences, "analytics" | "ads">) => {
  if (typeof window === "undefined") return;

  window.gtag?.("consent", "update", {
    analytics_storage: preferences.analytics ? "granted" : "denied",
    ad_storage: preferences.ads ? "granted" : "denied",
    ad_user_data: preferences.ads ? "granted" : "denied",
    ad_personalization: preferences.ads ? "granted" : "denied",
  });
};

const savePreferences = (preferences: Pick<ConsentPreferences, "analytics" | "ads">) => {
  if (typeof window === "undefined") return;

  const payload: ConsentPreferences = {
    ...preferences,
    updated_at: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Consent choices should still be applied even if browser storage is unavailable.
  }

  applyGoogleConsent(payload);
};

const GoogleConsentManager = () => {
  const [hasChoice, setHasChoice] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [ads, setAds] = useState(false);

  useEffect(() => {
    const stored = readPreferences();

    if (stored) {
      setAnalytics(stored.analytics);
      setAds(stored.ads);
      setHasChoice(true);
      setIsOpen(false);
      return;
    }

    setHasChoice(false);
    setIsOpen(true);
  }, []);

  const commit = (nextAnalytics: boolean, nextAds: boolean) => {
    savePreferences({ analytics: nextAnalytics, ads: nextAds });
    setAnalytics(nextAnalytics);
    setAds(nextAds);
    setHasChoice(true);
    setIsOpen(false);
    setShowDetails(false);
  };

  if (!isOpen) {
    if (!hasChoice) return null;

    return (
      <button
        type="button"
        onClick={() => {
          setShowDetails(true);
          setIsOpen(true);
        }}
        className="fixed bottom-3 left-3 z-[90] rounded-full border border-black/10 bg-white/85 px-2.5 py-1 text-[10px] font-medium tracking-wide text-neutral-600 shadow-sm backdrop-blur-md transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-500 focus-visible:ring-offset-2"
        aria-label="Open privacy choices"
      >
        Privacy choices
      </button>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100]" role="dialog" aria-label="Privacy choices">
      <div className="mx-2 mb-2 overflow-hidden rounded-xl border border-black/10 bg-[#fcfbf8]/95 shadow-[0_-6px_24px_rgba(0,0,0,0.08)] backdrop-blur-xl sm:mx-0 sm:mb-0 sm:rounded-none sm:border-x-0 sm:border-b-0 sm:shadow-[0_-3px_16px_rgba(0,0,0,0.06)]">
        <div className="mx-auto max-w-7xl px-3 py-2.5 sm:px-6 sm:py-2.5 lg:px-8">
          <div className="sm:flex sm:items-center sm:gap-5">
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3 sm:items-center sm:justify-start sm:gap-2.5">
                <p className="shrink-0 font-serif text-sm text-neutral-950 sm:text-[15px]">Privacy choices</p>
                {hasChoice ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                      setShowDetails(false);
                    }}
                    className="shrink-0 text-[11px] text-neutral-500 underline-offset-4 hover:text-neutral-900 hover:underline sm:hidden"
                  >
                    Close
                  </button>
                ) : null}
                <p className="hidden min-w-0 text-xs leading-5 text-neutral-600 sm:block">
                  We use analytics and advertising cookies to improve your experience and understand booking intent.
                </p>
              </div>
              <p className="mt-0.5 pr-2 text-[11px] leading-4 text-neutral-600 sm:hidden">
                We use cookies for analytics and advertising.
              </p>
            </div>

            {!showDetails ? (
              <div className="mt-2 flex items-center gap-1.5 sm:mt-0 sm:shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDetails(true)}
                  className="h-8 flex-1 px-2 text-[11px] text-neutral-700 sm:flex-none sm:px-3 sm:text-xs"
                >
                  Manage
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => commit(false, false)}
                  className="h-8 flex-1 border-black/15 bg-white/70 px-2 text-[11px] sm:flex-none sm:px-3 sm:text-xs"
                >
                  Reject
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => commit(true, true)}
                  className="h-8 flex-1 px-2.5 text-[11px] sm:flex-none sm:px-3.5 sm:text-xs"
                >
                  Accept all
                </Button>
                {hasChoice ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                      setShowDetails(false);
                    }}
                    className="hidden shrink-0 text-[11px] text-neutral-500 underline-offset-4 hover:text-neutral-900 hover:underline sm:block"
                  >
                    Close
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>

          {showDetails ? (
            <div className="mt-2.5 border-t border-black/10 pt-2.5">
              <div className="grid gap-1.5 sm:grid-cols-3 sm:gap-2">
                <div className="flex items-center justify-between gap-4 rounded-lg bg-black/[0.025] px-3 py-2">
                  <div>
                    <p className="text-xs font-medium text-neutral-900">Necessary</p>
                    <p className="mt-0.5 text-[10px] leading-4 text-neutral-500">Required for core site and security functions.</p>
                  </div>
                  <span className="shrink-0 text-[10px] font-medium text-neutral-500">Always on</span>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-lg border border-black/10 bg-white/55 px-3 py-2">
                  <div>
                    <p className="text-xs font-medium text-neutral-900">Analytics</p>
                    <p className="mt-0.5 text-[10px] leading-4 text-neutral-500">Allows Google Analytics storage for visit and site-use measurement.</p>
                  </div>
                  <Switch checked={analytics} onCheckedChange={setAnalytics} aria-label="Allow analytics cookies" />
                </div>

                <div className="flex items-center justify-between gap-4 rounded-lg border border-black/10 bg-white/55 px-3 py-2">
                  <div>
                    <p className="text-xs font-medium text-neutral-900">Advertising</p>
                    <p className="mt-0.5 text-[10px] leading-4 text-neutral-500">Allows Google Ads storage, ad measurement, and personalization signals.</p>
                  </div>
                  <Switch checked={ads} onCheckedChange={setAds} aria-label="Allow advertising cookies" />
                </div>
              </div>

              <div className="mt-2 flex items-center gap-1.5 sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => commit(analytics, ads)}
                  className="h-8 flex-1 border-black/15 bg-white/70 px-2.5 text-[11px] sm:flex-none sm:px-3 sm:text-xs"
                >
                  Save choices
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => commit(false, false)}
                  className="h-8 flex-1 border-black/15 bg-white/70 px-2.5 text-[11px] sm:flex-none sm:px-3 sm:text-xs"
                >
                  Reject
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => commit(true, true)}
                  className="h-8 flex-1 px-2.5 text-[11px] sm:flex-none sm:px-3.5 sm:text-xs"
                >
                  Accept all
                </Button>
                {hasChoice ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                      setShowDetails(false);
                    }}
                    className="ml-1 hidden shrink-0 text-[11px] text-neutral-500 underline-offset-4 hover:text-neutral-900 hover:underline sm:block"
                  >
                    Close
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default GoogleConsentManager;
